import { Text, useToaster } from '@innovationdepartment/proxima-ui';
import pluralize from 'pluralize';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  BRAND_PRODUCTS_PAGE_SIZE_DEFAULT,
  type BrandProductMetricAttributes,
  type BrandProductMetricResults,
  type ProductTaxonomyAttributes
} from 'types/brandProducts';
import useHandleApi from './useHandleApi';
import useProductTaxonomy from './useProductTaxonomy';

type Pagination = {
  page: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

const getDefaultPagination = (): Pagination => ({
  page: 0,
  totalPages: 1,
  totalItems: 0,
  hasNext: false,
  hasPrevious: false
});

const useBrandProducts = () => {
  const { taxonomies, taxonomyPagination, taxonomyLoading, upsertProductTaxonomy, ...taxonomyFns } =
    useProductTaxonomy();
  const { brandId } = useParams<'brandId'>();
  const [products, setProducts] = useState<BrandProductMetricAttributes[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<
    Map<string, BrandProductMetricAttributes>
  >(new Map());
  const [productsPagination, setProductsPagination] = useState<Pagination>(getDefaultPagination());
  const [productLoading, setProductLoading] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const { showToaster } = useToaster();

  const { handleApi } = useHandleApi();

  const selected = useMemo(() => Array.from(selectedProducts.values()), [selectedProducts]);

  // === Product Pagination Handlers ===
  const fetchProducts = async (page = 1) => {
    setProductLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('size', `${BRAND_PRODUCTS_PAGE_SIZE_DEFAULT}`);
      params.append('page', `${page}`);

      /* TODO(Jenky): Replace with SDK at some point */
      const response = await handleApi({
        endpoint: `/brand/${brandId}/products?${params.toString()}`,
        method: 'GET'
      });
      const { data } = response;
      const result = data as BrandProductMetricResults;

      const totalPages = Math.max(1, Math.ceil(result.total / BRAND_PRODUCTS_PAGE_SIZE_DEFAULT));
      setProductsPagination({
        page,
        totalPages,
        hasNext: page < totalPages,
        hasPrevious: page > 1,
        totalItems: result.total
      });
      setSelectedProducts(new Map());
      setProducts(result.products);
      tableRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    } catch {
      /* */
    }
    setProductLoading(false);
  };

  const onFetchProductsBatch = () => fetchProducts(1);
  const onNextProductsBatch = () => {
    fetchProducts(productsPagination.page + 1);
  };

  const onPreviousProductsBatch = () => {
    fetchProducts(productsPagination.page - 1);
  };

  // === Upsert Product Taxonomy ===
  const updateProductTaxonomyInternally = (taxonomyToUpdateTo: ProductTaxonomyAttributes) => {
    setProducts(product =>
      product.map(p => {
        if (!selectedProducts.has(p.productId)) return p;

        /* the client can only build a partial override; the server fills in the rest */
        return {
          ...p,
          override: {
            ...p.override,
            taxonomyClassification: taxonomyToUpdateTo.hierarchy
          } as NonNullable<BrandProductMetricAttributes['override']>
        };
      })
    );
  };

  const upsertProductTaxonomies = async (taxonomyId: string) => {
    if (!brandId) return;
    setProductLoading(true);
    await upsertProductTaxonomy(
      Array.from(selectedProducts.values()).map(p => ({
        productId: p.productId,
        taxonomyId,
        brandId
      }))
    );

    const taxonomyToUpdateTo = taxonomies.find(t => t.id === +taxonomyId);

    if (!taxonomyToUpdateTo) return;

    updateProductTaxonomyInternally(taxonomyToUpdateTo);

    showToaster({
      message: (
        <Text>
          {`${pluralize('product', selectedProducts.size, true)} ${pluralize('category', selectedProducts.size)} ${pluralize('have', selectedProducts.size)} been updated with ${taxonomyToUpdateTo.name}!`}
        </Text>
      ),
      variant: 'success'
    });
    setProductLoading(false);
  };

  const onProductRowSelect = async (product: BrandProductMetricAttributes) => {
    const isSelected = selectedProducts.has(product.productId);
    if (isSelected) {
      setSelectedProducts(prev => {
        const newSelected = new Map(prev);
        newSelected.delete(product.productId);
        return newSelected;
      });
    } else {
      setSelectedProducts(prev => new Map(prev).set(product.productId, product));
    }
  };

  const clearSelectedProducts = () => setSelectedProducts(new Map());

  return {
    tableRef,
    productLoading,
    taxonomyLoading,
    products,
    selectedProducts: selected,
    taxonomies,
    productsPagination,
    taxonomyPagination,
    onProductRowSelect,
    onFetchProductsBatch,
    onNextProductsBatch,
    onPreviousProductsBatch,
    upsertProductTaxonomies,
    clearSelectedProducts,
    ...taxonomyFns
  };
};

export default useBrandProducts;
